// Phase L — Outbound API Dependencies — v2 MUI (LDCP-160).
//
// Two tabs: Providers / By kind.
//
// Backend wiring preserved verbatim:
//   asec.dependenciesList(siteId, { days })   -> { items: [...] }
//   asec.dependenciesRecent(siteId, provider) -> { items: [...] }

import React from "react";
import {
  Stack, Box, Typography, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Drawer, IconButton, Chip, Grid, Select, MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  GitFork, Layers, Activity, Sparkles, ShieldAlert, Download, Search,
  Info, ArrowUpRight, Minus, Database, CreditCard, Users, KeyRound,
  MessageSquare, BarChart3, CircleDashed, X,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import { GhostButton } from "../../../../common/Button";
import AsecPage from "./AsecPage";
import {
  asecTabsSx,
  filterSelectSx,
  asecSettingsTableHeadSx,
  asecSettingsTableBodySx,
} from "./asecUi";
import {
  AsecMetricStrip,
  AsecSectionCard,
  AsecSettingsTableCard,
  AsecMono,
  AsecHeroStatValue,
} from "./components/asecPrimitives";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";

const KIND_META = {
  storage:    { Icon: Database,       palette: { bg: colors.green[10], fg: colors.green[300], bd: "rgba(31,193,107,0.30)" } },
  payments:   { Icon: CreditCard,     palette: { bg: colors.red[10], fg: colors.red[200], bd: "rgba(251,55,72,0.30)" } },
  crm:        { Icon: Users,          palette: { bg: colors.primary[10], fg: colors.primary[400], bd: colors.primary[200] } },
  ai:         { Icon: Sparkles,       palette: { bg: "#F3EEFE", fg: "#6B3FA0", bd: "#E2D6FA" } },
  auth:       { Icon: KeyRound,       palette: { bg: "#EAF0FE", fg: "#3A56C4", bd: "#CFDBFB" } },
  comms:      { Icon: MessageSquare,  palette: { bg: "#FFF0F5", fg: "#B0337A", bd: "#FAD2E4" } },
  email:      { Icon: MessageSquare,  palette: { bg: "#FFF0F5", fg: "#B0337A", bd: "#FAD2E4" } },
  analytics:  { Icon: BarChart3,      palette: { bg: colors.yellow[10], fg: "#7a5b10", bd: "rgba(223,180,0,0.40)" } },
  monitoring: { Icon: BarChart3,      palette: { bg: colors.yellow[10], fg: "#7a5b10", bd: "rgba(223,180,0,0.40)" } },
  cdn:        { Icon: Database,       palette: { bg: colors.green[10], fg: colors.green[300], bd: "rgba(31,193,107,0.30)" } },
  search:     { Icon: Search,         palette: { bg: "#FFF1E6", fg: "#B25A1A", bd: "#FBD9BE" } },
  maps:       { Icon: Search,         palette: { bg: colors.yellow[10], fg: "#7a5b10", bd: "rgba(223,180,0,0.40)" } },
  ci:         { Icon: GitFork,        palette: { bg: colors.neutral[50], fg: colors.neutral[1000], bd: colors.neutral[200] } },
};

const PROVIDER_LOGO_COLOR = {
  aws: "#FF9900", stripe: "#635BFF", zendesk: "#03363D", openai: "#10A37F",
  auth0: "#EB5424", anthropic: "#D97757", s3: "#569A31", twilio: "#F22F46",
  salesforce: "#00A1E0", segment: "#52BD95", datadog: "#632CA6",
  cloudflare: "#F38020", github: "#181717", slack: "#4A154B",
  shopify: "#7AB55C", hubspot: "#FF7A59", mailchimp: "#FFE01B",
  sendgrid: "#1A82E2", okta: "#007DC1", stripeconnect: "#635BFF",
};

const KIND_PALETTE = ["#22A85B", "#6371F7", "#8257C4", "#D00416", "#B0337A", "#F2B429", "#3A56C4"];
const TAB_KEYS = ["providers", "kinds"];

function hashColor(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360},60%,42%)`;
}

function ProviderLogo({ name }) {
  const norm = String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const bg = PROVIDER_LOGO_COLOR[norm] || hashColor(norm);
  const initials = (name || "?").split(/[\s-]/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase();
  return (
    <Box sx={{
      width: 30, height: 30, borderRadius: "8px", display: "flex",
      alignItems: "center", justifyContent: "center", flexShrink: 0,
      fontWeight: 800, fontSize: 14, color: "#fff", backgroundColor: bg,
    }}>
      {initials || "?"}
    </Box>
  );
}

function KindTag({ kind }) {
  const meta = KIND_META[kind] || { Icon: Info, palette: { bg: colors.neutral[50], fg: colors.neutral[1000], bd: colors.neutral[200] } };
  const { Icon } = meta;
  const p = meta.palette;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      fontSize: 12, fontWeight: 600,
      px: 1.25, py: 0.375, borderRadius: "6px",
      backgroundColor: p.bg, color: p.fg, border: `1px solid ${p.bd}`,
    }}>
      <LayerdIcon icon={Icon} size="xs" color="inherit" />
      {kind || "—"}
    </Box>
  );
}

function KindBar({ kindCounts }) {
  const total = Object.values(kindCounts).reduce((a, b) => a + b, 0) || 1;
  const entries = Object.entries(kindCounts).sort((a, b) => b[1] - a[1]);
  return (
    <Box sx={{ display: "flex", height: 8, borderRadius: "4px", overflow: "hidden", mt: 0.75 }}>
      {entries.map(([k, v], i) => (
        <Box key={k} title={`${k} · ${v}`} sx={{
          height: "100%", width: `${Math.max(2, (v / total) * 100)}%`,
          backgroundColor: KIND_PALETTE[i % KIND_PALETTE.length],
        }} />
      ))}
    </Box>
  );
}

function Spark({ color, hits }) {
  const base = Math.max(1, Math.min(12, hits || 1));
  const pts = `0,${14 - base} 12,${14 - Math.max(1, base - 1)} 24,${14 - base} 36,${14 - Math.max(1, base - 2)} 48,${14 - Math.max(1, base - 1)} 60,${14 - Math.max(1, base - 3)}`;
  return (
    <svg width="48" height="14" viewBox="0 0 60 14" style={{ verticalAlign: "middle" }}>
      <polyline fill="none" stroke={color || "#A8ADBA"} strokeWidth="1.5" points={pts} />
    </svg>
  );
}

function HeroMetrics({ rows, days }) {
  const total = rows.reduce((a, r) => a + Number(r.hits || r.hits_7d || r.hits_count || 0), 0);
  const kindCounts = {};
  rows.forEach((r) => { kindCounts[r.kind || "other"] = (kindCounts[r.kind || "other"] || 0) + 1; });
  const distinctKinds = Object.keys(kindCounts).length;
  const cutoff = Date.now() - days * 86400 * 1000;
  const newThisWindow = rows.filter((r) => r.first_seen && new Date(r.first_seen).getTime() >= cutoff);

  return (
    <AsecMetricStrip metrics={[
      {
        key: "providers",
        label: "Providers seen",
        icon: GitFork,
        highlight: true,
        value: <AsecHeroStatValue primary={rows.length} secondary="of 85 catalog" />,
        sub: <KindBar kindCounts={kindCounts} />,
      },
      {
        key: "kinds",
        label: "Distinct kinds",
        icon: Layers,
        value: <AsecHeroStatValue primary={distinctKinds} secondary="/ 13" />,
        sub: Object.keys(kindCounts).slice(0, 4).join(" · ") || "—",
      },
      {
        key: "hits",
        label: `Total hits · ${days}d`,
        icon: Activity,
        value: total.toLocaleString(),
        sub: (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <LayerdIcon icon={Minus} size="xs" color="neutral" />
            flat vs. prior {days}d
          </Stack>
        ),
      },
      {
        key: "new",
        label: "New this window",
        icon: Sparkles,
        value: newThisWindow.length,
        valueSx: { color: colors.primary[300] },
        sub: newThisWindow.slice(0, 2).map((r) => r.provider).join(" · ") || "—",
      },
      {
        key: "review",
        label: "Needs review",
        icon: ShieldAlert,
        value: "—",
        valueSx: { color: "#7a5b10" },
        sub: "Not available yet",
      },
    ]} />
  );
}

function ProvidersTab({ rows, days, search, setSearch, kindFilter, setKindFilter, kindCounts, onViewRequests, onExport }) {
  const filtered = rows.filter((r) => {
    if (kindFilter !== "all" && r.kind !== kindFilter) return false;
    if (search && !((r.provider || "") + " " + (r.kind || "")).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const kindChips = [
    { key: "all", label: "All kinds", ct: rows.length },
    ...Object.entries(kindCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([k, ct]) => ({ key: k, label: k, ct })),
  ];

  return (
    <>
      <AsecEventsFilterBar
        sx={{ mb: 1.75 }}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search provider…"
        categoryFilter={kindFilter}
        onCategoryChange={setKindFilter}
        chips={kindChips}
        action={(
          <GhostButton size="small" startIcon={<LayerdIcon icon={Download} size="sm" color="neutral" />} onClick={onExport}>
            Export
          </GhostButton>
        )}
      />

      <AsecSettingsTableCard>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                <TableCell sx={asecSettingsTableHeadSx}>Kind</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Provider</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Risk</TableCell>
                <TableCell sx={asecSettingsTableHeadSx} align="right">Hits · {days}d</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>First seen</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Last seen</TableCell>
                <TableCell sx={asecSettingsTableHeadSx} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5, color: colors.neutral[700] }}>
                    {rows.length === 0
                      ? <>No third-party API providers detected in the last {days}d. Either no responses contain third-party URLs, OR body sampling isn&apos;t on for this site.</>
                      : "No providers match this filter."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r, idx) => {
                const hits = Number(r.hits || r.hits_7d || r.hits_count || 0);
                return (
                  <TableRow
                    key={`${r.provider}-${idx}`}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => onViewRequests(r)}
                  >
                    <TableCell sx={asecSettingsTableBodySx}><KindTag kind={r.kind} /></TableCell>
                    <TableCell sx={asecSettingsTableBodySx}>
                      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ fontWeight: 600 }}>
                        <ProviderLogo name={r.provider} />
                        {r.provider}
                      </Stack>
                    </TableCell>
                    <TableCell sx={asecSettingsTableBodySx}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, fontWeight: 500, color: colors.green[300] }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[300] }} />
                        Reviewed
                      </Stack>
                    </TableCell>
                    <TableCell sx={asecSettingsTableBodySx} align="right">
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                        <Spark hits={hits} />
                        <Typography sx={{ fontWeight: 600, minWidth: 20, textAlign: "right" }}>{hits}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={asecSettingsTableBodySx}>
                      <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
                        {r.first_seen ? new Date(r.first_seen).toLocaleDateString() : "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>
                        {r.first_seen ? new Date(r.first_seen).toLocaleTimeString() : ""}
                      </Typography>
                    </TableCell>
                    <TableCell sx={asecSettingsTableBodySx}>
                      <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
                        {r.last_seen ? new Date(r.last_seen).toLocaleDateString() : "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>
                        {r.last_seen ? new Date(r.last_seen).toLocaleTimeString() : ""}
                      </Typography>
                    </TableCell>
                    <TableCell sx={asecSettingsTableBodySx} align="right">
                      <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end" sx={{ color: colors.primary[300], fontWeight: 500, fontSize: 12 }}>
                        View requests
                        <LayerdIcon icon={ArrowUpRight} size="sm" color="primary" />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 2.25, py: 1.5, borderTop: `1px solid ${colors.neutral[200]}`, fontSize: 12, color: colors.neutral[700] }}>
          {rows.length} providers · <strong style={{ color: colors.neutral[1000] }}>{Object.keys(kindCounts).length}</strong> kinds · catalog of 85 across 13 kinds
        </Box>
      </AsecSettingsTableCard>
    </>
  );
}

function KindCard({ kind, providers, count, color }) {
  const fillPct = Math.min(100, count * 50);
  return (
    <Box sx={{
      border: `1px solid ${colors.neutral[200]}`, borderRadius: "11px", p: 1.75,
      display: "flex", flexDirection: "column", gap: 1,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <KindTag kind={kind} />
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{count} provider{count === 1 ? "" : "s"}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{count}</Typography>
      <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{providers.join(" · ")}</Typography>
      <Box sx={{ height: 6, borderRadius: "3px", backgroundColor: colors.neutral[50], overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${fillPct}%`, backgroundColor: color, borderRadius: "3px" }} />
      </Box>
    </Box>
  );
}

function KindsTab({ rows }) {
  const byKind = React.useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = r.kind || "other";
      m[k] = m[k] || { providers: [], count: 0 };
      m[k].providers.push(r.provider);
      m[k].count += 1;
    });
    return m;
  }, [rows]);

  const seenKinds = Object.keys(byKind);
  const allCatalogKinds = ["storage", "payments", "crm", "ai", "auth", "comms", "analytics", "email", "cdn", "search", "maps", "ci", "monitoring"];
  const unseenKinds = allCatalogKinds.filter((k) => !seenKinds.includes(k));

  return (
    <AsecSectionCard
      title="Egress by kind"
      icon={Layers}
      radius="12px"
      right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{seenKinds.length} of 13 catalog kinds observed</Typography>}
      noPadding
      bodySx={{ p: 2.25 }}
    >
      <Grid container spacing={1.75}>
        {seenKinds.map((k, i) => {
          const data = byKind[k];
          return (
            <Grid item xs={12} sm={6} md={3} key={k}>
              <KindCard kind={k} providers={data.providers} count={data.count} color={KIND_PALETTE[i % KIND_PALETTE.length]} />
            </Grid>
          );
        })}
        {unseenKinds.length > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{
              border: `1px dashed ${colors.neutral[200]}`, borderRadius: "11px", p: 1.75,
              display: "flex", flexDirection: "column", gap: 1, opacity: 0.55,
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.75,
                  fontSize: 12, fontWeight: 600,
                  px: 1.25, py: 0.375, borderRadius: "6px",
                  backgroundColor: colors.neutral[50], color: colors.neutral[700],
                  border: `1px solid ${colors.neutral[200]}`,
                }}>
                  <LayerdIcon icon={CircleDashed} size="xs" color="neutral" />
                  {unseenKinds.length} more
                </Box>
                <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>0 seen</Typography>
              </Stack>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.neutral[500] }}>0</Typography>
              <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{unseenKinds.slice(0, 6).join(" · ")}</Typography>
              <Box sx={{ height: 6, borderRadius: "3px", backgroundColor: colors.neutral[50] }} />
            </Box>
          </Grid>
        )}
      </Grid>
    </AsecSectionCard>
  );
}

function RecentDrawer({ open, provider, rows, onClose, loading }) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 560, maxWidth: "94vw" } }}>
      <Stack sx={{ height: "100%" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between"
          sx={{ px: 2.75, py: 2.5, borderBottom: `1px solid ${colors.neutral[200]}`, gap: 1.5 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.125}>
              <ProviderLogo name={provider?.provider} />
              <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
                Recent requests · {provider?.provider}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 12, color: colors.neutral[700], mt: 0.75 }}>
              Most recent <AsecMono>outbound_api_refs</AsecMono> rows for this provider.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <LayerdIcon icon={X} size="sm" color="neutral" />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
          {loading && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} sx={{ color: colors.primary[300] }} />
            </Stack>
          )}
          {!loading && rows.length === 0 && (
            <Typography sx={{ p: 4, textAlign: "center", color: colors.neutral[700] }}>
              No recent requests recorded for {provider?.provider}.
            </Typography>
          )}
          {!loading && rows.map((r, i) => (
            <Box key={i} sx={{ px: 2.75, py: 1.25, borderBottom: `1px solid ${colors.neutral[200]}` }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>
                  {r.ts ? new Date(r.ts).toLocaleString() : "—"}
                </Typography>
                {r.method && (
                  <Chip size="small" label={r.method} sx={{
                    fontSize: 12, fontWeight: 700,
                    height: 20, backgroundColor: colors.primary[10], color: colors.primary[400],
                    border: `1px solid ${colors.primary[200]}`,
                  }} />
                )}
                {r.status != null && (
                  <Typography sx={{
                    fontSize: 12, fontWeight: 600,
                    color: r.status >= 400 ? colors.red[200] : colors.green[300],
                  }}>
                    {r.status}
                  </Typography>
                )}
              </Stack>
              <Typography sx={{ fontSize: 12, wordBreak: "break-all", mt: 0.5 }}>
                {r.raw_path || r.url || r.path || "—"}
              </Typography>
              {r.src_ip && (
                <Typography sx={{ fontSize: 12, color: colors.neutral[700], mt: 0.5 }}>
                  src · {r.src_ip}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Stack>
    </Drawer>
  );
}

function Body({ siteId, days }) {
  const { showError } = useNotification();
  const [tab, setTab] = React.useState("providers");
  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [kindFilter, setKindFilter] = React.useState("all");
  const [drawer, setDrawer] = React.useState(null);
  const [drawerRows, setDrawerRows] = React.useState([]);
  const [drawerLoading, setDrawerLoading] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await asec.dependenciesList(siteId, { days });
      setRows((data && data.items) || []);
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    }
  }, [siteId, days, showError]);
  React.useEffect(() => { reload(); }, [reload]);

  const kindCounts = React.useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.kind || "other"] = (m[r.kind || "other"] || 0) + 1; });
    return m;
  }, [rows]);

  const exportInventory = React.useCallback(() => {
    const csv = ["kind,provider,first_seen,last_seen,hits"];
    rows.forEach((r) => {
      const hits = r.hits || r.hits_7d || r.hits_count || 0;
      csv.push([r.kind, r.provider, r.first_seen, r.last_seen, hits].join(","));
    });
    const blob = new Blob([csv.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dependencies-${siteId}-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, siteId, days]);

  const openProvider = async (r) => {
    setDrawer(r);
    setDrawerRows([]);
    setDrawerLoading(true);
    try {
      const { data } = await asec.dependenciesRecent(siteId, r.provider);
      setDrawerRows((data && data.items) || []);
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setDrawerLoading(false);
    }
  };

  const tabIndex = TAB_KEYS.indexOf(tab);

  return (
    <Stack spacing={2.5}>
      <HeroMetrics rows={rows} days={days} />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Tabs value={tabIndex >= 0 ? tabIndex : 0} onChange={(_, v) => setTab(TAB_KEYS[v])} sx={asecTabsSx}>
          <Tab label={`Providers (${rows.length})`} />
          <Tab label={`By kind (${Object.keys(kindCounts).length})`} />
        </Tabs>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: "none", md: "flex" }, pr: 0.5 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[200] }} />
          <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
            DLP sampling on · <AsecMono>request_body=true</AsecMono>
          </Typography>
        </Stack>
      </Stack>

      {tab === "providers" && (
        <ProvidersTab
          rows={rows}
          days={days}
          search={search}
          setSearch={setSearch}
          kindFilter={kindFilter}
          setKindFilter={setKindFilter}
          kindCounts={kindCounts}
          onViewRequests={openProvider}
          onExport={exportInventory}
        />
      )}
      {tab === "kinds" && <KindsTab rows={rows} />}

      <RecentDrawer
        open={!!drawer}
        provider={drawer}
        rows={drawerRows}
        loading={drawerLoading}
        onClose={() => setDrawer(null)}
      />
    </Stack>
  );
}

export default function Dependencies() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [days, setDays] = React.useState(7);

  return (
    <AsecPage
      featureKey="api.openapi_validation"
      helpTooltip="Outbound API SBOM. Pingora scans request and response bodies (when DLP sampling is on) against a closed catalog of third-party API providers and writes hits to outbound_api_refs."
      title="Dependencies"
      helmetTitle="API Security — Dependencies"
      pageDescription={(
        <>
          SOC2 / ISO27001-grade outbound inventory. Request and response bodies (when DLP sampling is on)
          are scanned against a closed catalog of <strong>85</strong> third-party API
          providers across <strong>13</strong> kinds, and hits are written to <AsecMono>outbound_api_refs</AsecMono>.
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
      headerActions={(
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Select
            size="small"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ ...filterSelectSx, minWidth: 120 }}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
          </Select>
        </Stack>
      )}
    >
      {(siteId) => (
        <Body
          key={`${siteId}-${refreshKey}-${days}`}
          siteId={siteId}
          days={days}
        />
      )}
    </AsecPage>
  );
}
